import React, { useEffect, useMemo, useState } from "react";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
  useMaterialReactTable,
} from "material-react-table";

import { Box, Button } from "@mui/material";
import { useExportSensors, useGetSensor } from "../../../hooks/sensors";
import { Sensor } from "../../../types/sensors";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

const SensorsTable = () => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [globalFilter, setGlobalFilter] = useState<string | undefined>("");
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  useEffect(() => {
    setPagination({
      pageIndex: 0,
      pageSize: 10,
    });
  }, [columnFilters]);

  const {
    data: sensorQueryResults = [],
    isError: isLoadingSensorsError,
    isFetching: isFetchingSensors,
    isLoading: isLoadingSensors,
  } = useGetSensor({ pagination, columnFilters, sorting, globalFilter });

  const fetchedSensors = sensorQueryResults[0]?.sensors || [];

  const exportSensors = useExportSensors(columnFilters, sorting);

  const handleExport = async () => {
    try {
      await exportSensors();
    } catch (error) {
      toast.error("Failed to export");
    } finally {
    }
  };

  useEffect(() => {
    const count = sensorQueryResults[0]?.rowCount;
    if (count == 0) {
      setRowCount(0);
    }
    if (!count || count === rowCount) {
      return;
    }
    setRowCount(count);
  }, [sensorQueryResults]);

  const tableName = "sensors_table";
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const tableState = JSON.parse(localStorage.getItem("tableState") || "{}");
    return (
      tableState[tableName]?.columnVisibility || {
        id: false,
      }
    );
  });

  useEffect(() => {
    localStorage.setItem(
      "tableState",
      JSON.stringify({
        ...JSON.parse(localStorage.getItem("tableState") || "{}"),
        [tableName]: {
          columnVisibility,
        },
      })
    );
  }, [columnVisibility, tableName]);

  const columns = useMemo<MRT_ColumnDef<Sensor>[]>(
    () => [
      {
        accessorKey: "id",
        header: "Id",
        enableEditing: false,
        enableSorting: false,
        size: 80,
      },

      {
        accessorKey: "name",
        header: "Name",
        size: 220,
        enableEditing: (row) => !row.original.id,
        Cell: ({ row }) => {
          return <>{row.original.name}</>;
        },
        muiEditTextFieldProps: {
          type: "text",
          required: true,
          error: !!validationErrors?.name,
          helperText: validationErrors?.name,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              name: undefined,
            }),
        },
      },
      {
        accessorKey: "last_seen",
        header: "Last Seen",
        size: 140,
        enableColumnFilter: true,
        enableSorting: true,
        muiEditTextFieldProps: {
          type: "text",
        },
      },

      {
        accessorKey: "tags",
        header: "Tags",
        size: 140,
        enableColumnFilter: true,
        enableSorting: true,
        muiEditTextFieldProps: {
          type: "text",
        },
      },
    ],
    [validationErrors]
  );

  const table = useMaterialReactTable({
    columns,
    enableGlobalFilter: false,
    data: fetchedSensors,
    initialState: {
      showColumnFilters: true,
      density: "compact",
      columnVisibility: columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    createDisplayMode: "modal",
    editDisplayMode: "modal",
    selectAllMode: "all",
    enableColumnResizing: true,
    getRowId: (row) => row.id,
    rowNumberDisplayMode: "original",
    muiToolbarAlertBannerProps: isLoadingSensorsError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount,
    muiTableContainerProps: {
      sx: {
        minHeight: "500px",
      },
    },
    renderBottomToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "1rem",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Button variant="contained" onClick={handleExport}>
          Export {rowCount} Rows
        </Button>
      </Box>
    ),

    state: {
      columnFilters,
      globalFilter,
      pagination,
      sorting,
      columnVisibility: columnVisibility,
      isLoading: isLoadingSensors,
      showAlertBanner: isLoadingSensorsError,
      showProgressBars: isFetchingSensors,
    },
  });

  return <MaterialReactTable table={table} />;
};

export default SensorsTable;
